import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AppLayout from "@/components/AppLayout";
import BilingualText from "@/components/BilingualText";
import ReadinessMeter from "@/components/ReadinessMeter";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { IDS } from "@/constants/testIds";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

const SKILL_NAMES = {
  reading: "Reading", vocabulary: "Vocabulary", grammar: "Grammar",
  listening: "Listening", writing: "Writing", speaking: "Speaking",
};

export default function ParentDashboard() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [code, setCode] = useState("");
  const [linking, setLinking] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadChildren = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/parent/children");
      setChildren(data || []);
      if (data?.length && !selected) setSelected(data[0].id);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [selected]);

  const loadDashboard = useCallback(async (childId) => {
    try {
      const { data } = await api.get(`/parent/child/${childId}/dashboard`);
      setDashboard(data);
    } catch { setDashboard(null); }
  }, []);

  useEffect(() => { loadChildren(); }, [loadChildren]);
  useEffect(() => { if (selected) loadDashboard(selected); }, [selected, loadDashboard]);

  const linkChild = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLinking(true);
    try {
      const { data } = await api.post("/parent/link", { code: code.trim() });
      toast.success(`Linked ${data.child_name}! 关联成功`);
      setCode("");
      loadChildren();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link failed");
    } finally { setLinking(false); }
  };

  const chartSeries = (dashboard?.recent || [])
    .slice()
    .reverse()
    .map((a) => ({
      date: (a.created_at || "").slice(5, 10),
      score: a.score ?? null,
      skill: a.skill,
    }));

  return (
    <AppLayout>
      <div data-testid={IDS.parent.root} className="space-y-10">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-2xl bg-amber-100 grid place-items-center">
            <Users className="w-5 h-5 text-amber-700" />
          </span>
          <BilingualText cn="家长面板" en="Parent Dashboard" size="hero" />
        </div>

        <section className="card-plush p-6">
          <BilingualText cn="关联孩子账户" en="Link a child account" size="md" />
          <form onSubmit={linkChild} className="mt-4 flex flex-wrap gap-3 items-center">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABCD1234"
              className="max-w-xs rounded-full font-bold tracking-widest"
              data-testid={IDS.parent.linkCodeInput}
            />
            <Button
              type="submit"
              disabled={linking || !code.trim()}
              className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
              data-testid={IDS.parent.linkSubmit}
            >
              {linking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              <span className="cn mr-2">关联</span>Link
            </Button>
            <p className="text-xs text-slate-500">
              <span className="cn">让孩子在自己的首页复制关联码给你。</span>{" "}
              Ask your child to copy their code from their dashboard.
            </p>
          </form>
        </section>

        {loading ? (
          <div className="grid place-items-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : children.length === 0 ? (
          <div className="card-plush p-10 text-center">
            <BilingualText
              cn="尚未关联任何孩子。"
              en="No children linked yet."
              size="md"
            />
          </div>
        ) : (
          <>
            <section className="flex flex-wrap gap-2">
              {children.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelected(c.id)}
                  data-testid={IDS.parent.child(c.id)}
                  className={
                    "rounded-full px-4 py-2 text-sm font-bold border-2 transition-colors " +
                    (selected === c.id
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-400")
                  }
                >
                  {c.name} · {c.level || "—"}
                </button>
              ))}
            </section>

            {dashboard && (
              <>
                <section className="grid md:grid-cols-12 gap-6">
                  <div className="md:col-span-4">
                    <ReadinessMeter
                      value={dashboard.progress?.readiness || 0}
                      subtitle={`${dashboard.progress?.total_attempts || 0} attempts · 次练习`}
                    />
                  </div>
                  <div className="md:col-span-8 card-plush p-6">
                    <BilingualText cn="各项技能得分" en="Skill scores" size="md" />
                    <div className="mt-6 grid sm:grid-cols-2 gap-4">
                      {Object.entries(dashboard.progress?.skill_avg || {}).map(([s, v]) => (
                        <div key={s}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="font-bold text-slate-600">{SKILL_NAMES[s] || s}</span>
                            <span className="font-black text-slate-900">{Math.round(v)}</span>
                          </div>
                          <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-sky-400 to-emerald-400"
                              style={{ width: `${Math.min(100, v)}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                <section className="card-plush p-6">
                  <BilingualText cn="最近成绩趋势" en="Recent score trend" size="md" />
                  <div className="mt-4 h-64">
                    <ResponsiveContainer>
                      <LineChart data={chartSeries}>
                        <CartesianGrid strokeDasharray="4 6" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="score"
                          stroke="#3b82f6"
                          strokeWidth={3}
                          dot={{ r: 4, fill: "#3b82f6" }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </section>

                <section className="card-plush p-6">
                  <BilingualText cn="需要加强的方向" en="Focus areas" size="md" />
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(dashboard.progress?.weak_areas || []).length ? (
                      dashboard.progress.weak_areas.map((s) => (
                        <span key={s} className="badge-soft bg-rose-50 text-rose-700">
                          {SKILL_NAMES[s] || s}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-slate-400">
                        <span className="cn">练习数据更多之后再显示。</span>{" "}
                        Practice more to see focus areas.
                      </span>
                    )}
                  </div>
                </section>
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
